/**
 * Application route paths
 * Copyright (c) 2025 Medidata Solutions, Inc.
 */

/**
 * Application base path
 * Hash routing doesn't use basename - the hash (#) handles all routing
 * The app is served from /apps/edge-rcm/app/ or /app/ but routes use hash fragments
 */
export const APP_BASENAME = '' as string

/**
 * Relative route paths for router configuration
 */
export const ROUTE_PATHS = {
  ROOT: '/', // Root path - index loader redirects to appropriate page based on role
  LOGIN: '/Login', // Use absolute path for top-level login route (capital L matches legacy)
  NEW_ACCOUNT: '/new-account/:hash', // New account setup with token (external: /New-account/:hash)
  RESET_PASSWORD: '/Reset-password/:hash', // Reset password with token (legacy URL: /Reset-password/:hash)
  FORGOT_PASSWORD: '/forgot-password', // Forgot password page
  OVERVIEW: 'overview',
  NOTIFICATIONS: 'notifications',
  DOCUMENTS: 'documents/:documentsTab',
  TASKS: {
    ROOT: 'Tasks',
    TasksTab: ':tasksTab',
    DETAIL: 'detail/:id' // No leading slash here!
  },
  WORKFLOWS: 'ChangeRequests/:workflowsTab',
  ETMF: {
    ROOT: 'clinical', // Updated to match your entry.config logic
    WITH_TAB: 'clinical/:etmfTab'
  },
  REPORTS: 'reports/:reportTab',
  SETTINGS: {
    ROOT: 'settings',
    WITH_TAB: 'settings/:settingsTab'
  },
  SYSTEM: {
    ROOT: 'system',
    PERFORMANCE: 'system-performance', // Relative to 'system'
    POLICIES: 'policies',
    ETMF_SETUP: 'EtmfSetup',
    TEXT_MESSAGES: 'strings',
    INTEGRATIONS: 'integrations',
    OPERATING_MODE: 'operating-mode',
    USERS: 'users',
    GROUPS: 'groups'
  },
  NOT_FOUND: '*'
} as const

/**
 * Absolute route paths for navigation.
 * When using basename: "/!/app", React Router automatically
 * prepends the basename to these strings.
 */
export const ROUTES = {
  ROOT: '/',
  LOGIN: '/login',
  NEW_ACCOUNT: '/new-account',
  FORGOT_PASSWORD: '/forgot-password',
  OVERVIEW: '/overview',
  NOTIFICATIONS: '/notifications', // Added leading slash for consistent absolute navigation
  DOCUMENTS: '/documents',
  TASKS: '/Tasks',
  WORKFLOWS: '/ChangeRequests',
  ETMF: '/clinical',
  REPORTS: '/reports',
  SETTINGS: '/settings',
  SYSTEM: {
    ROOT: '/system',
    PERFORMANCE: '/system/system-performance',
    POLICIES: '/system/policies',
    ETMF_SETUP: '/system/EtmfSetup',
    TEXT_MESSAGES: '/system/strings',
    INTEGRATIONS: '/system/integrations',
    OPERATING_MODE: '/system/operating-mode',
    USERS: '/system/users',
    GROUPS: '/system/groups'
  }
} as const

export const SYSTEM_ROUTES = ROUTE_PATHS.SYSTEM
